import { cn } from '@repo/ui/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const cardVariants = cva('rounded-lg border shadow-sm', {
  variants: {
    variant: {
      default:
        'border-primary-200 bg-white text-primary-950 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-50',
      passive: 'border-primary-100 bg-white',
      secondary:
        'bg-primary-300 bg-opacity-20 border-primary-200 text-primary-950 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-50',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export type CardVariant = VariantProps<typeof cardVariants>['variant']

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { variant?: CardVariant }
>(({ className, variant, ...props }, ref) => (
  <div
    tabIndex={-1}
    ref={ref}
    className={cn(cardVariants({ variant }), className)}
    {...props}
  />
))
Card.displayName = 'Card'

const cardHeaderClasses = 'flex flex-shrink-0 p-4 pb-0 xl:p-6 xl:pb-0'
const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const hasCardAction = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === CardAction,
  )

  if (hasCardAction) {
    const cardAction = React.Children.toArray(children).find(
      (child) => React.isValidElement(child) && child.type === CardAction,
    )
    const otherChildren = React.Children.toArray(children).filter(
      (child) => !(React.isValidElement(child) && child.type === CardAction),
    )

    return (
      <div
        tabIndex={-1}
        ref={ref}
        className={cn(
          cardHeaderClasses,
          'items-center justify-between gap-4',
          className,
        )}
        {...props}
      >
        <div className="flex flex-1 flex-col space-y-1.5">{otherChildren}</div>
        <div className="flex-shrink-0">{cardAction}</div>
      </div>
    )
  }

  return (
    <div
      tabIndex={-1}
      ref={ref}
      className={cn(cardHeaderClasses, 'flex-col space-y-1.5', className)}
      {...props}
    >
      {children}
    </div>
  )
})
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-primary-600 select-none text-xl font-semibold leading-none tracking-tight xl:text-2xl',
      className,
    )}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-primary-400 dark:text-primary-300 select-none text-sm',
      className,
    )}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    tabIndex={-1}
    ref={ref}
    className={cn('text-primary p-4 xl:p-6', className)}
    {...props}
  />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-4 pt-0 xl:p-6 xl:pt-0', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

const CardAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center space-x-2', className)}
    {...props}
  />
))
CardAction.displayName = 'CardAction'

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
}
